import * as buttons from 'smg/buttons';
import * as template from 'smg/template';
import * as html from 'smg/html';
import * as phone from 'smg/phone';
import * as models from 'smg/models';

import axios from 'smg/axios';
import { ReportValidity } from 'smg/form';

import { HandleRequestError } from 'smg/errors';
import Modal from 'smg/modal';
import { ClearCache, Session } from 'smg/customer-api/session';
import { FullName, GetFirstAndLast } from 'smg/users';

if (document.body.id === 'account-profile-settings') {
    void main();
}

async function main() {
    // Profile Section

    const $profilePreview = document.querySelector('#profile-preview') as HTMLDivElement;
    const $name = $profilePreview.querySelector('#profile-name') as HTMLDivElement;
    const $company = $profilePreview.querySelector('#profile-company') as HTMLDivElement;
    const $email = $profilePreview.querySelector('#profile-email') as HTMLDivElement;
    const $phone = $profilePreview.querySelector('#profile-phone') as HTMLDivElement;
    const $editProfile = $profilePreview.querySelector('#edit-profile-btn') as HTMLButtonElement;

    const $profileEdit = document.querySelector('#profile-edit') as HTMLDivElement;
    const $profileEditForm = $profileEdit.querySelector('#profile-edit-form') as HTMLFormElement;
    const $editName = $profileEditForm.querySelector('#profile-edit-name') as HTMLInputElement;
    const $editCompany = $profileEditForm.querySelector('#profile-edit-company') as HTMLInputElement;
    const $editEmail = $profileEditForm.querySelector('#profile-edit-email') as HTMLInputElement;
    const $editPhone = $profileEditForm.querySelector('#profile-edit-phone') as HTMLInputElement;
    const $editBack = $profileEditForm.querySelector('#profile-edit-back') as HTMLButtonElement;
    const $editSubmit = $profileEditForm.querySelector('#profile-edit-submit') as HTMLButtonElement;
    const $editSpinner = document.querySelector('#profile-edit-spinner-container') as HTMLElement;

    const p = new phone.Phone($editPhone);

    const refreshInfo = async () => {
        $editSpinner.classList.remove('d-none');
        ClearCache();
        await Session().then(sess => {
            $name.textContent = html.Escape(FullName(sess));
            $company.textContent = html.Escape(sess.company || '-');
            $email.textContent = html.Escape(sess.email);
            $phone.textContent = html.Escape(phone.Human(sess.phone));

            $editName.value = FullName(sess);
            $editCompany.value = sess.company;
            $editEmail.value = sess.email;
            void p.Iti.promise.then(() => p.Iti.setNumber(sess.phone));
        }).finally(() => {
            $editSpinner.classList.add('d-none');
        });
    };

    void refreshInfo();

    $editProfile.addEventListener('click', () => {
        $profilePreview.classList.add('d-none');
        $profileEdit.classList.remove('d-none');
        $editName.focus();
        $editName.setSelectionRange(0, $editName.value.length);
    });

    const toPreview = () => {
        $profilePreview.classList.remove('d-none');
        $profileEdit.classList.add('d-none');
        $editSpinner.classList.add('d-none');
        void refreshInfo();
    };

    $editBack.addEventListener('click', () => {
        toPreview();
    });

    $profileEditForm.noValidate = true;
    $profileEditForm.addEventListener('submit', async e => {
        e.preventDefault();
        if (!ReportValidity($profileEditForm)) {
            return false;
        }
        buttons.Start($editSubmit);
        $editSpinner.classList.remove('d-none');
        [$editName, $editCompany, $editEmail, $editPhone].forEach($ => {
            $.disabled = true;
        });
        const { firstName, lastName } = GetFirstAndLast($editName.value);
        await axios.patch('/current-user', {
            firstName,
            lastName,
            company: $editCompany.value,
            email: $editEmail.value,
            phone: await p.Format(),
        }).then(() => {
            new Modal({
                width: '38.5rem',
                body: template.Clone('#profile-edit-success'),
                confirmText: 'OK',
                appendConfirmButtonClass: 'w-100 w-sm-auto btn-sm-lg',
                cancelButton: false,
                appendClassBySelector: {
                    '.modal-dialog': 'px-4 px-6',
                    '.modal-body': 'px-6 pt-8 pb-2',
                    '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
                },
                onClose: toPreview,
                onSubmit: toPreview,
            });
        }).catch(err => HandleRequestError(err, $profileEditForm)).finally(() => {
            $editSpinner.classList.add('d-none');
            buttons.Stop($editSubmit);
            [$editName, $editCompany, $editEmail, $editPhone].forEach($ => {
                $.disabled = false;
            });
        });
    });

    // Notification Section

    const $allowSMS = document.querySelector<HTMLInputElement>('#allow-sms') as HTMLInputElement;
    const $allowEmail = document.querySelector<HTMLInputElement>('#allow-email') as HTMLInputElement;
    const $emailDisclaimer = document.querySelector<HTMLElement>('#email-disclaimer') as HTMLElement;
    const $preferredContactMethodParent = document.querySelector<HTMLSelectElement>('#preferred-method-parent') as HTMLSelectElement;
    const $preferredContactMethod = document.querySelector<HTMLSelectElement>('#preferred-method') as HTMLSelectElement;

    interface userFlagsResp {
        flagIDs: number[];
    }

    await axios.get<userFlagsResp>('/users/flags').then(resp => {
        let flagIDs = resp.data.flagIDs;
        if (flagIDs === null) {
            flagIDs = [];
        }
        if (flagIDs.includes(models.FlagID.FLAG_PREFERS_SMS)) {
            $allowSMS.checked = true;
        } else {
            $preferredContactMethodParent.classList.add('d-none');
            $preferredContactMethod.disabled = true;
        }

        if (flagIDs.includes(models.FlagID.FLAG_UNSUBSCRIBED)) {
            $emailDisclaimer.classList.remove('d-none');
            $allowEmail.checked = false;
        } else {
            $emailDisclaimer.classList.add('d-none');
            $allowEmail.checked = true;
        }

        if (flagIDs.includes(models.FlagID.FLAG_PREFERS_EMAIL) === flagIDs.includes(models.FlagID.FLAG_PREFERS_PHONE)) {
            // Treat both or neither as "no preference"
            $preferredContactMethod.value = '';
        } else if (flagIDs.includes(models.FlagID.FLAG_PREFERS_EMAIL)) {
            $preferredContactMethod.value = 'email';
        } else if (flagIDs.includes(models.FlagID.FLAG_PREFERS_PHONE)) {
            $preferredContactMethod.value = 'sms';
        }
    }).catch(err => HandleRequestError(err));

    $allowSMS.addEventListener('change', async () => {
        if ($allowSMS.checked) {
            await axios.post(`/flags/${models.FlagID.FLAG_PREFERS_SMS}`).catch(HandleRequestError);
            $preferredContactMethodParent.classList.remove('d-none');
            $preferredContactMethod.disabled = false;
        } else {
            await axios.delete(`/flags/${models.FlagID.FLAG_PREFERS_SMS}`).catch(HandleRequestError);
            $preferredContactMethodParent.classList.add('d-none');
            $preferredContactMethod.disabled = true;
        }
    });

    // This one is backwards because the flag is "unsubscribed"
    $allowEmail.addEventListener('change', async () => {
        if ($allowEmail.checked) {
            await axios.delete(`/flags/${models.FlagID.FLAG_UNSUBSCRIBED}`).catch(HandleRequestError);
            $emailDisclaimer.classList.add('d-none');
        } else {
            await axios.post(`/flags/${models.FlagID.FLAG_UNSUBSCRIBED}`).catch(HandleRequestError);
            $emailDisclaimer.classList.remove('d-none');
        }
    });

    $preferredContactMethod.addEventListener('change', async () => {
        if ($preferredContactMethod.value === 'email') {
            await axios.post(`/flags/${models.FlagID.FLAG_PREFERS_EMAIL}`).catch(HandleRequestError);
            await axios.delete(`/flags/${models.FlagID.FLAG_PREFERS_PHONE}`).catch(HandleRequestError);
        } else if ($preferredContactMethod.value === 'sms') {
            await axios.post(`/flags/${models.FlagID.FLAG_PREFERS_PHONE}`).catch(HandleRequestError);
            await axios.delete(`/flags/${models.FlagID.FLAG_PREFERS_EMAIL}`).catch(HandleRequestError);
        } else if ($preferredContactMethod.value === '') {
            await axios.delete(`/flags/${models.FlagID.FLAG_PREFERS_EMAIL}`).catch(HandleRequestError);
            await axios.delete(`/flags/${models.FlagID.FLAG_PREFERS_PHONE}`).catch(HandleRequestError);
        }
    });

    // Password Section
    const $spinner = document.querySelector<HTMLElement>('#reset-password-spinner-container') as HTMLElement;
    const $passwordForm = document.querySelector<HTMLFormElement>('#change-password') as HTMLFormElement;
    const $newPassword1 = $passwordForm.querySelector<HTMLInputElement>('#new-password') as HTMLInputElement;
    const $newPassword2 = $passwordForm.querySelector<HTMLInputElement>('#confirm-password') as HTMLInputElement;
    const $submit = $passwordForm.querySelector<HTMLButtonElement>('[type="submit"]') as HTMLButtonElement;

    $passwordForm.noValidate = true;
    $passwordForm.addEventListener('submit', async e => {
        e.preventDefault();

        $newPassword2.setCustomValidity('');
        if ($newPassword1.value !== $newPassword2.value) {
            $newPassword2.setCustomValidity('Passwords do not match');
        }

        $newPassword1.setCustomValidity('');

        if (!ReportValidity($passwordForm)) {
            return false;
        }

        buttons.Start($submit);
        $spinner.classList.remove('d-none');

        await axios.patch('/current-user/password', {
            'newPassword': $newPassword1.value,
            'confirmPassword': $newPassword2.value,
        }).then(() => {
            $newPassword1.value = '';
            $newPassword2.value = '';

            new Modal({
                width: '38.5rem',
                body: template.Clone('#password-change-success'),
                confirmText: 'OK',
                appendConfirmButtonClass: 'w-100 w-sm-auto btn-sm-lg',
                cancelButton: false,
                appendClassBySelector: {
                    '.modal-dialog': 'px-4 px-6',
                    '.modal-body': 'px-6 pt-8 pb-2',
                    '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
                },
            });
            $spinner.classList.add('d-none');
        }).catch(err => HandleRequestError(err, $passwordForm)).
            finally(() => {
                buttons.Stop($submit);
            });
    });
}
